import { v4 as uuidv4 } from 'uuid';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { until } from '@vueuse/core';
import config from '@/config/api';
import configKeys from '@/config/widgets';
import { ApiError } from '@/api/errors';
import { NetworkError } from '@/api/networkError';
import { CsrfTokenHeader, CsrfTokenValue, RiskledgerTrackingIdHeader } from '@/api/headerKeys';
import {
  mergeQueryOptions,
  mergeMutationOptions,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import { SESSION_QUERY } from './api/queryKeys';
import { toReauthRequiredError } from './api/reauthError';
import type { User } from '@@/packages/shared/types/models/user';
import type { Organisation } from '@@/packages/shared/types/models/organisation';

// BFF cookie endpoints are session-cookie based (no Bearer token), so they need
// `credentials: 'include'` but not the Authorization header. We deliberately do
// NOT route through `@/api/request` / `fetchClient`: that wrapper redirects to
// logout on a 401, which must not happen mid-login or during a boolean session
// probe. We still mirror the request-tracing headers fetchClient sets (request
// id, client build, tracking id) and capture the tracking id from the response,
// so the auth funnel stays traceable.
async function cookieRequest<TData>(method: string, path: string, body?: unknown) {
  const headers = new Headers({ 'Content-Type': 'application/json' });
  const trackingID = sessionStorage.getItem(RiskledgerTrackingIdHeader);
  if (trackingID) {
    headers.set(RiskledgerTrackingIdHeader, trackingID);
  }

  headers.set('Riskledger-Request-Id', uuidv4());
  headers.set('Riskledger-Client-Build', configKeys.buildSHA);
  headers.set(CsrfTokenHeader, CsrfTokenValue);

  const init: RequestInit = { method, credentials: 'include', headers };
  if (method !== 'GET' && body !== undefined) {
    init.body = JSON.stringify(body);
  }

  // Mirror fetchClient: wrap a transport-level fetch rejection (dropped
  // connection, offline, blocked by an extension) in NetworkError so Sentry
  // fingerprints it consistently
  let res: Response;
  try {
    res = await fetch(config.base_url + path, init);
  } catch (cause) {
    throw new NetworkError('Network request failed', { cause, method, path });
  }

  const responseTrackingID = res.headers.get(RiskledgerTrackingIdHeader);
  if (responseTrackingID && !sessionStorage.getItem(RiskledgerTrackingIdHeader)) {
    sessionStorage.setItem(RiskledgerTrackingIdHeader, responseTrackingID);
  }

  if (!res.ok) {
    const text = await res.text().catch(() => undefined);
    let errorData: unknown = { message: 'Request failed' };
    if (text !== undefined) {
      try {
        errorData = JSON.parse(text);
      } catch {
        errorData = { message: text };
      }
    }
    throw new ApiError('Request failed', {
      status: res.status,
      data: errorData,
      headers: res.headers,
    });
  }
  // 204 / No Content — the MFA verify endpoints intentionally return
  // no body; calling res.json() on them throws. Return undefined and
  // let the typed callers ignore the body.
  if (res.status === 204) {
    return { data: undefined as TData, status: res.status, headers: res.headers };
  }
  // A body read can drop mid-stream just like the request, so it gets the same
  // NetworkError. Reading text and parsing separately keeps a malformed body a
  // SyntaxError rather than passing it off as a transport failure.
  let text: string;
  try {
    text = await res.text();
  } catch (cause) {
    throw new NetworkError('Network request failed', { cause, method, path });
  }
  const data: TData = JSON.parse(text);
  return { data, status: res.status, headers: res.headers };
}

// The trimmed org shape the login funnel returns (in the orgs picker and pending
// sessions). Shared by both LoginResponse and SessionState below.
export interface OrgOption {
  id: string;
  name: string;
  logoUrl?: string;
}

export type SessionState =
  | { status: 'active'; user: User; org: Organisation; sso: boolean }
  | {
      status: 'pending';
      user: Pick<User, 'userID' | 'email' | 'firstName' | 'lastName'>;
      orgs: OrgOption[];
    }
  | { status: 'unauthenticated' };

// The raw /auth/session fetch + response-shape mapping. This is the queryFn behind
// useSession(); consume the composable (not this) so reads share the query cache.
export async function getSession(): Promise<SessionState> {
  try {
    const { data } = await cookieRequest<{
      user: User;
      org?: Organisation;
      orgs?: OrgOption[];
      sso?: boolean;
    }>('GET', '/auth/session');
    return data.org !== undefined
      ? { status: 'active', user: data.user, org: data.org, sso: data.sso ?? false }
      : { status: 'pending', user: data.user, orgs: data.orgs ?? [] };
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return { status: 'unauthenticated' };
    }
    throw error;
  }
}

// Session caching is delegated to TanStack Query rather than a hand-rolled TTL.
// Router beforeEach guards call isAuthenticated()/isAuthenticatedByRL() several
// times per navigation; a short staleTime collapses that burst into a single
// /auth/session request. The login mutations and logout invalidate the cached
// session so a cookie change is reflected on the very next read.
const SESSION_STALE_TIME = 1000;

export function useSession(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SESSION_QUERY],
      queryFn: getSession,
      staleTime: SESSION_STALE_TIME,
      // retry off + handle errors via resolvedSession() so a failing /auth/session
      // surfaces immediately to the auth circuit-breaker rather than being retried.
      retry: false,
      throwOnError: false,
    }),
  );

  // Lets a guard `await` a definitive answer (like useCurrentOrg().resolvedOrg()).
  // We wait for the query to settle on FRESH data — not a stale cached value a
  // just-mounted observer is still revalidating — or to error. This is what makes a
  // post-login/logout invalidation effective: the invalidated entry forces a refetch
  // on mount and we wait for it, rather than returning the old session. A 401 maps to
  // { status: 'unauthenticated' } (a value, not an error), so only transport/5xx
  // failures reject here and feed the circuit-breaker.
  async function resolvedSession(): Promise<SessionState> {
    await until(
      () =>
        query.isError.value ||
        (query.isSuccess.value && !query.isStale.value && !query.isFetching.value),
    ).toBe(true);
    if (query.isError.value) {
      throw query.error.value ?? new Error('Failed to fetch session');
    }
    return query.data.value as SessionState;
  }

  return { ...query, resolvedSession };
}

// Returns a function that drops the cached session so the next read re-fetches.
// Same injection-context rule as useFetchSession: capture in setup / guard head,
// invoke later (e.g. after an awaited logout request).
export function useInvalidateSession() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [SESSION_QUERY] });
}

// The three shapes /auth/login (and the SSO login endpoints) can return: a
// completed login, an auth challenge to satisfy, or a list of orgs to pick from.
//
// requiredAttributes are the attributes Cognito will accept with the challenge
// answer, as bare names ("phone_number"); sending any it considers settled gets
// the whole response rejected (FEDX-261). Empty array vs absent field matters
// to callers; see loginFlowStore.requiredAttributes.
export type LoginResponse =
  | { user: User; org: Organisation }
  | { challenge: string; session: string; requiredAttributes?: string[] }
  | { orgs: OrgOption[] };

// Which endpoint started the login. The login endpoints never report SSO-ness
// (only GET /auth/session does, via SessionState.sso), so the frontend carries
// it. Doubles as the `type` meta on the auth:login analytics event.
export type LoginMethod = 'cookie' | 'SAML SSO' | 'OAuth SSO';

export async function login(email: string, password: string): Promise<LoginResponse> {
  // Cognito usernames are case-sensitive and every user-creating path standardises
  // first, so an as-typed address misses the account outright (FEDX-306).
  const { data } = await cookieRequest<LoginResponse>('POST', '/auth/login', {
    email: email.toLowerCase().trim(),
    password,
  });
  return data;
}

type LoginRequest = { email: string; password: string };

export function useLogin(options?: MutationOptions<LoginResponse, LoginRequest>) {
  const queryClient = useQueryClient();
  return useMutation(
    mergeMutationOptions(options, {
      mutationFn: ({ email, password }) => login(email, password),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [SESSION_QUERY] }),
    }),
  );
}

export async function ssoLoginCookie(code: string, state?: string): Promise<LoginResponse> {
  // `state` is an opaque, signed, short-lived token minted by the backend for a
  // forced org-switch re-login. Echo it back verbatim; omit it entirely for a
  // normal login so the request body is unchanged.
  const { data } = await cookieRequest<LoginResponse>('POST', '/sso/login', {
    code,
    ...(state ? { state } : {}),
  });
  return data;
}

export function useSsoLogin(
  options?: MutationOptions<LoginResponse, { code: string; state?: string }>,
) {
  const queryClient = useQueryClient();
  return useMutation(
    mergeMutationOptions(options, {
      mutationFn: ({ code, state }) => ssoLoginCookie(code, state),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [SESSION_QUERY] }),
    }),
  );
}

export async function ssoOAuthLoginCookie(code: string): Promise<LoginResponse> {
  const { data } = await cookieRequest<LoginResponse>('POST', '/sso/oauth/login', { code });
  return data;
}

export function useSsoOAuthLogin(options?: MutationOptions<LoginResponse, string>) {
  const queryClient = useQueryClient();
  return useMutation(
    mergeMutationOptions(options, {
      mutationFn: (code) => ssoOAuthLoginCookie(code),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [SESSION_QUERY] }),
    }),
  );
}

export type ChallengeName = 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA' | 'NEW_PASSWORD_REQUIRED';

export async function challenge(
  challengeName: ChallengeName,
  challengeResponses: Record<string, string>,
  session: string,
): Promise<LoginResponse> {
  const { data } = await cookieRequest<LoginResponse>('POST', '/auth/login/challenge', {
    challenge_name: challengeName,
    session,
    challenge_responses: challengeResponses,
  });
  return data;
}

type ChallengeRequest = {
  challengeName: ChallengeName;
  challengeResponses: Record<string, string>;
  session: string;
};

export function useChallenge(options?: MutationOptions<LoginResponse, ChallengeRequest>) {
  const queryClient = useQueryClient();
  return useMutation(
    mergeMutationOptions(options, {
      mutationFn: ({ challengeName, challengeResponses, session }) =>
        challenge(challengeName, challengeResponses, session),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [SESSION_QUERY] }),
    }),
  );
}

// The select-org endpoint resolves a pending session into an active one.
type SelectOrgResponse = { user: User; org: Organisation };

export async function selectOrg(orgId: string): Promise<SelectOrgResponse> {
  try {
    const { data } = await cookieRequest<SelectOrgResponse>('POST', '/auth/login/select-org', {
      org_id: orgId,
    });
    return data;
  } catch (err) {
    throw toReauthRequiredError(err) ?? err;
  }
}

export function useSelectOrg(options?: MutationOptions<SelectOrgResponse, string>) {
  const queryClient = useQueryClient();
  return useMutation(
    mergeMutationOptions(options, {
      mutationFn: (orgId) => selectOrg(orgId),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [SESSION_QUERY] }),
    }),
  );
}

export async function logout(): Promise<void> {
  try {
    await cookieRequest('POST', '/auth/logout');
  } catch (e) {
    // Logout is idempotent, so a failure must not block the redirect — but it
    // means the backend may not have cleared the HttpOnly rl_sid cookie, which
    // is the usual cause of an eternal logout/redirect loop. Surface it rather
    // than swallow it silently so the failure is diagnosable.
    logError(e);
  }
}

// MFA enrolment — cookie-authenticated. The verify endpoints sync the
// MFA preference server-side, so SESSION_QUERY must be invalidated on
// success: the next guard read picks up the new MFA-enrolled state and
// the requireAuthWithoutMFAPreference guard stops bouncing the user
// back to /auth/mfa/setup.

export async function mfaTotpSetup(): Promise<{ secretCode: string }> {
  const { data } = await cookieRequest<{ secretCode: string }>('POST', '/auth/mfa/totp/setup');
  return data;
}

export function useMfaTotpSetup(options?: MutationOptions<{ secretCode: string }, void>) {
  return useMutation(
    mergeMutationOptions(options, {
      mutationFn: () => mfaTotpSetup(),
    }),
  );
}

type MfaTotpVerifyRequest = { code: string; deviceName?: string };

export async function mfaTotpVerify(code: string, deviceName?: string): Promise<void> {
  await cookieRequest('POST', '/auth/mfa/totp/verify', { code, deviceName });
}

export function useMfaTotpVerify(options?: MutationOptions<void, MfaTotpVerifyRequest>) {
  const queryClient = useQueryClient();
  return useMutation(
    mergeMutationOptions(options, {
      mutationFn: ({ code, deviceName }) => mfaTotpVerify(code, deviceName),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [SESSION_QUERY] }),
    }),
  );
}

export async function mfaSmsSetup(phoneNumber: string): Promise<void> {
  await cookieRequest('POST', '/auth/mfa/sms/setup', { phoneNumber });
}

export async function mfaSmsVerify(code: string): Promise<void> {
  await cookieRequest('POST', '/auth/mfa/sms/verify', { code });
}

export function useMfaSmsVerify(options?: MutationOptions<void, string>) {
  const queryClient = useQueryClient();
  return useMutation(
    mergeMutationOptions(options, {
      mutationFn: (code) => mfaSmsVerify(code),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [SESSION_QUERY] }),
    }),
  );
}

// The mid-login MFA_SETUP challenge endpoints
// (`/auth/mfa/setup/{associate,verify}`) are intentionally NOT wired
// here: handling that challenge requires a QR + code UI that doesn't
// exist in the cookie-login funnel. Add them when (and if) that flow
// is built.

export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  await cookieRequest('POST', '/auth/change-password', { oldPassword, newPassword });
}

// Cookie-mode replacement for Amplify's fetchUserAttributes, read by the
// SMS-MFA setup flow to prefill the user's phone number. The BFF reads these
// live from Cognito (via AdminGetUser); /auth/session doesn't carry them. The
// interface mirrors the BFF's camelCase shape (verified flags nullable: null =
// the attribute isn't set on the Cognito user).
export interface UserAttributes {
  sub: string;
  email: string;
  emailVerified: boolean | null;
  phoneNumber?: string;
  phoneNumberVerified: boolean | null;
  givenName: string;
  familyName: string;
}

export async function getUserAttributes(): Promise<UserAttributes> {
  const { data } = await cookieRequest<UserAttributes>('GET', '/auth/user-attributes');
  return data;
}

// Signup & password-reset BFF endpoints (FEDX-113). Replace the Amplify
// signUp / confirmSignUp / resendSignUpCode / resetPassword /
// confirmResetPassword calls. All return 204
// with no body and create no session (confirm's subsequent useLogin()
// establishes one). Email is lowercased/trimmed FE-side so the
// signup → confirm → login chain agrees on the Cognito username.

export interface CookieSignupParams {
  userId: string;
  organisationId: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  invite: string;
  marketing: boolean;
}

export async function cookieSignup(params: CookieSignupParams): Promise<void> {
  await cookieRequest('POST', '/auth/signup', {
    ...params,
    email: params.email.toLowerCase().trim(),
  });
}

export async function cookieConfirmSignup(email: string, code: string): Promise<void> {
  await cookieRequest('POST', '/auth/signup/confirm', {
    email: email.toLowerCase().trim(),
    code,
  });
}

export async function cookieResendSignupCode(email: string): Promise<void> {
  await cookieRequest('POST', '/auth/signup/resend-code', {
    email: email.toLowerCase().trim(),
  });
}

export async function cookieForgotPassword(email: string): Promise<void> {
  await cookieRequest('POST', '/auth/forgot-password', {
    email: email.toLowerCase().trim(),
  });
}

export async function cookieResetPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await cookieRequest('POST', '/auth/reset-password', {
    email: email.toLowerCase().trim(),
    code,
    newPassword,
  });
}
